/**
 * 跨部署後動態 chunk 404 的自癒(R225 起;R413 抽出共用)
 * =============================================================================
 * 事故與原理見 [lazyWithReload](../app/lazyWithReload.ts) 檔頭:`/assets/*` 是 hash 檔名 + 一年
 * immutable 快取,**舊分頁的主 bundle 寫死了舊 chunk 的檔名**,換 image 之後那些檔不存在 →
 * 一載入就 404。標準解法 = 偵測到載入失敗 → 強制重載一次(`index.html` 是 no-store,重載就拿到
 * 新主 bundle);同一支 chunk 一個 session 只給一次,第二次照樣失敗就把錯誤丟出去(避免無限重載)。
 *
 * R413 起除了 lazy route,還有兩個「用到才載入」的點也走這裡:Firebase SDK(第一次即時訂閱)
 * 與 en / ja 字典(切語言)。少了它,換版後開著的舊分頁會「即時更新整段失效」或「切英文沒反應」。
 */

const FLAG_PREFIX = 'hai.chunkReload.';

/**
 * 登記「要為這支 chunk 重載了」;回傳 true = 可以重載,false = 這個 session 已經試過。
 * `sessionStorage` 不可用(隱私模式 / sandbox)→ false(寧可讓錯誤浮出來,也不要無限重載)。
 */
export function markChunkReloadAttempt(chunkName: string): boolean {
  try {
    const key = `${FLAG_PREFIX}${chunkName}`;
    if (sessionStorage.getItem(key) === '1') return false;
    sessionStorage.setItem(key, '1');
    return true;
  } catch {
    return false;
  }
}

/**
 * 動態 import 的自癒包裝:失敗時先重載一次(回一個永不 resolve 的 promise,頁面正在重載),
 * 這個 session 已試過就照原樣丟錯。
 *
 * @param chunkName    sessionStorage 旗標的鍵(每支 chunk 各自一次額度)
 * @param factory      `() => import(...)`
 * @param beforeReload 重載前要先記下的東西(例:使用者正要切的語言,重載後才接得上)
 */
export async function importWithReload<T>(
  chunkName: string,
  factory: () => Promise<T>,
  beforeReload?: () => void,
): Promise<T> {
  try {
    return await factory();
  } catch (err) {
    if (markChunkReloadAttempt(chunkName)) {
      console.warn('[chunkReload] chunk load failed — reloading once for a fresh bundle', {
        chunkName,
        err,
      });
      beforeReload?.();
      window.location.reload();
      return new Promise<T>(() => {});
    }
    throw err;
  }
}
